import { useTranslation } from 'react-i18next';

import { Modal } from '../common/Modal';

export interface ConfirmAdminDialogProps {
  open: boolean;
  /** Who is about to become admin — display name or UPN, whatever we have. */
  userLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * "Are you sure?" gate in front of granting the admin role. Admin is the one
 * role that can do everything — including minting more admins — so a slip of
 * the mouse in a role <select> should not be enough to hand it out.
 *
 * The body below is mounted only while open (same shape as RejectDialog), so
 * each opening starts fresh, with focus on Cancel: Enter backs out, granting
 * takes a deliberate click.
 */
export function ConfirmAdminDialog({
  open,
  ...props
}: ConfirmAdminDialogProps) {
  if (!open) {
    return null;
  }
  return <ConfirmAdminDialogBody {...props} />;
}

function ConfirmAdminDialogBody({
  userLabel,
  onConfirm,
  onCancel,
}: Omit<ConfirmAdminDialogProps, 'open'>) {
  const { t } = useTranslation();

  // Escape and a click beside the panel cancel (the native <dialog> closes,
  // Modal reports it); focus then goes back to the role <select>.
  return (
    <Modal
      role="alertdialog"
      aria-label={t('admin.users.confirmAdmin.title')}
      onClose={onCancel}
      className="backdrop:bg-ink/40"
    >
      <div className="w-full max-w-md rounded-xl bg-card p-6 text-ink">
        <h3 className="mb-2 text-base font-semibold text-ink">
          {t('admin.users.confirmAdmin.title')}
        </h3>
        <p className="text-sm text-ink-soft">
          {t('admin.users.confirmAdmin.body', { user: userLabel })}
        </p>
        <p className="mt-2 text-sm font-medium text-magenta">
          {t('admin.users.confirmAdmin.warning')}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            autoFocus
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-blue/5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-magenta px-3 py-1.5 text-xs font-medium text-ink hover:bg-magenta/85"
          >
            {t('admin.users.confirmAdmin.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
